import { Box, makeStyles } from "@material-ui/core";
import { useIntl } from "react-intl";
import voca from "voca";

import {
  MemberStatus,
  memberStatusColor,
} from "../../../settings/MemberStatus";
import { EnableStatus, statusColor } from "../../../settings/EnableStatus";

export enum StatusType {
  memberStatus = "memberStatus",
  enableStatus = "enableStatus",
}

interface StatusProps {
  type?: StatusType;
  status: MemberStatus | EnableStatus;
  size?: number;
  withBorder?: boolean;
  withUppercase?: boolean;
}

const useStyles = makeStyles(
  (theme) => ({
    root: {
      display: "inline",
      fontSize: (props: StatusProps) => (props.size ? props.size : 13),
      fontWeight: theme.typography.fontWeightBold,

      // Enable status

      "& .activated": {
        color: statusColor(EnableStatus.activated, 600),
        backgroundColor: (props: StatusProps) =>
          props.withBorder
            ? statusColor(EnableStatus.activated, 50)
            : "transparent",
        textTransform: (props: StatusProps) =>
          props.withUppercase ? "uppercase" : "inherit",

        "&.buttonVariant": {
          padding: "4px 12px",
          borderRadius: 2,
          border: `1px solid ${statusColor(EnableStatus.activated, 100)}`,
        },
      },

      "& .deactivated": {
        color: statusColor(EnableStatus.deactivated, 600),
        backgroundColor: (props: StatusProps) =>
          props.withBorder
            ? statusColor(EnableStatus.deactivated, 50)
            : "transparent",
        textTransform: (props: StatusProps) =>
          props.withUppercase ? "uppercase" : "inherit",

        "&.buttonVariant": {
          padding: "4px 12px",
          borderRadius: 2,
          border: `1px solid ${statusColor(EnableStatus.deactivated, 100)}`,
        },
      },

      // Members status

      "& .approved": {
        color: memberStatusColor(MemberStatus.approved, 600),
        backgroundColor: (props: StatusProps) =>
          props.withBorder
            ? memberStatusColor(MemberStatus.approved, 50)
            : "transparent",
        textTransform: (props: StatusProps) =>
          props.withUppercase ? "uppercase" : "inherit",

        "&.buttonVariant": {
          padding: "4px 12px",
          borderRadius: 2,
          border: `1px solid ${memberStatusColor(MemberStatus.approved, 100)}`,
        },
      },

      "& .new": {
        color: memberStatusColor(MemberStatus.new, 600),
        backgroundColor: (props: StatusProps) =>
          props.withBorder
            ? memberStatusColor(MemberStatus.new, 50)
            : "transparent",
        textTransform: (props: StatusProps) =>
          props.withUppercase ? "uppercase" : "inherit",

        "&.buttonVariant": {
          padding: "4px 12px",
          borderRadius: 2,
          border: `1px solid ${memberStatusColor(MemberStatus.new, 100)}`,
        },
      },

      "& .pending": {
        color: memberStatusColor(MemberStatus.pending, 600),
        backgroundColor: (props: StatusProps) =>
          props.withBorder
            ? memberStatusColor(MemberStatus.pending, 50)
            : "transparent",
        textTransform: (props: StatusProps) =>
          props.withUppercase ? "uppercase" : "inherit",

        "&.buttonVariant": {
          padding: "4px 12px",
          borderRadius: 2,
          border: `1px solid ${memberStatusColor(MemberStatus.pending, 100)}`,
        },
      },

      "& .declined": {
        color: memberStatusColor(MemberStatus.declined, 600),
        backgroundColor: (props: StatusProps) =>
          props.withBorder
            ? memberStatusColor(MemberStatus.declined, 50)
            : "transparent",
        textTransform: (props: StatusProps) =>
          props.withUppercase ? "uppercase" : "inherit",

        "&.buttonVariant": {
          padding: "4px 12px",
          borderRadius: 2,
          border: `1px solid ${memberStatusColor(MemberStatus.declined, 100)}`,
        },
      },

      "& .locked": {
        color: memberStatusColor(MemberStatus.locked, 600),
        backgroundColor: (props: StatusProps) =>
          props.withBorder
            ? memberStatusColor(MemberStatus.locked, 50)
            : "transparent",
        textTransform: (props: StatusProps) =>
          props.withUppercase ? "uppercase" : "inherit",

        "&.buttonVariant": {
          padding: "4px 12px",
          borderRadius: 2,
          border: `1px solid ${memberStatusColor(MemberStatus.locked, 100)}`,
        },
      },
    },
  }),
  { name: "app-status-styles" }
);

export function Status(props: StatusProps) {
  const classes = useStyles(props);
  const intl = useIntl();
  // Props
  const { status, withBorder, type } = props;

  const build = () => {
    if (type === StatusType.memberStatus) {
      switch (status) {
        case MemberStatus.approved:
          return [
            intl.formatMessage({ id: "sharedScreen.approved" }),
            "approved",
          ];
        case MemberStatus.new:
          return [intl.formatMessage({ id: "sharedScreen.new" }), "new"];
        case MemberStatus.pending:
          return [
            intl.formatMessage({ id: "sharedScreen.pending" }),
            "pending",
          ];
        case MemberStatus.locked:
          return [intl.formatMessage({ id: "sharedScreen.locked" }), "locked"];
        case MemberStatus.declined:
          return [
            intl.formatMessage({ id: "sharedScreen.declined" }),
            "declined",
          ];
      }
    }

    if (type === StatusType.enableStatus) {
      switch (status) {
        case EnableStatus.activated:
          return [
            intl.formatMessage({ id: "sharedScreen.activated" }),
            "activated",
          ];
        case EnableStatus.deactivated:
          return [
            intl.formatMessage({ id: "sharedScreen.deactivated" }),
            "deactivated",
          ];
      }
    }

    return ["", ""];
  };

  const [message, statusClassName] = build();

  return (
    <Box className={classes.root}>
      <Box
        component="span"
        display="inline-block"
        className={`${withBorder ? "buttonVariant" : ""} ${statusClassName}`}
      >
        {voca.titleCase(message)}
      </Box>
    </Box>
  );
}

Status.defaultProp = {
  type: StatusType.memberStatus,
  withBorder: false,
  withUppercase: false,
};
